import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import {
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  LinearProgress,
  TextField,
  Grid,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import axios from "../../utils/axios";
import { setNextStage } from "../../reducers/documento";

export default function ProcedureExtractor({ handleBack, canGoBack }) {
  const dispatch = useDispatch();
  const { transaction, participant } = useParams();

  const documento = useSelector(({ documento }) => documento.documento);
  const participants = useSelector(({ documento }) => documento.documento.participants);
  const gestores = useSelector(({ documento }) => documento.gestores);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  const [extractor, setExtractor] = useState(null);

  let participantData = null;
  if (participant && documento) {
    participantData = participants.find((it) => it.id === participant);
  } else if (documento) {
    participantData = participants[0];
  }

  const extractorId = participantData?.next?.["custom:extractor"];

  useEffect(() => {
    if (extractorId) {
      fetchExtractor();
    }
  }, [extractorId]);

  const fetchExtractor = async () => {
    try {
      const response = await axios.get(`/api/extraction/extractors/${extractorId}`);
      setExtractor(response.data.extractor);
    } catch (err) {
      console.error("Error fetching extractor:", err);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setExtractedData(null);

      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const handleExtract = async () => {
    if (!file) {
      setError("Por favor, sube un documento primero");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("document", file);
      formData.append("extractorId", extractorId);
      formData.append("procedureId", transaction);

      const response = await axios.post(`/api/extraction/extract`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      if (response.data.success) {
        setExtractedData(response.data.extractedData);
      } else {
        setError(response.data.error || "Error en la extracción");
      }
    } catch (err) {
      console.error("Error extracting:", err);
      setError(err.response?.data?.error || err.message || "Error al procesar el documento");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setExtractedData((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.fieldId === fieldId ? { ...f, value } : f)),
    }));
  };

  const handleSubmit = () => {
    if (!extractedData) {
      setError("Primero debes extraer los datos del documento");
      return;
    }

    const formData = {};
    extractedData.fields.forEach((field) => {
      formData[field.fieldId] = field.value;
    });

    let gestorId = participantData.id;
    let current = participantData.next.id;

    dispatch(setNextStage(transaction, { gestorId, current }, { form: formData }));
  };

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {participantData?.next?.name || "Extracción de Documento"}
          </Typography>

          {extractor && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Extractor: {extractor.name} - {extractor.documentType}
            </Typography>
          )}

          {!extractedData ? (
            <>
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${isDragActive ? "#1976d2" : "#ccc"}`,
                  borderRadius: "8px",
                  padding: "40px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
                  marginTop: "20px",
                  marginBottom: "20px",
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon style={{ fontSize: 48, color: "#1976d2" }} />
                <Typography variant="body1" style={{ marginTop: "10px" }}>
                  {isDragActive
                    ? "Suelta el archivo aquí..."
                    : "Arrastra un documento o haz clic para seleccionar"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Formatos: JPG, PNG, WebP, PDF (máx. 10MB)
                </Typography>
              </div>

              {file && (
                <div style={{ marginBottom: "20px" }}>
                  <Typography variant="body2">
                    Archivo seleccionado: <strong>{file.name}</strong>
                  </Typography>
                  {preview && (
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        marginTop: "10px",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                </div>
              )}

              {loading && (
                <div style={{ marginBottom: "20px" }}>
                  <LinearProgress />
                  <Typography variant="body2" style={{ marginTop: "10px", textAlign: "center" }}>
                    Procesando documento con IA...
                  </Typography>
                </div>
              )}

              {error && (
                <Typography color="error" style={{ marginBottom: "20px" }}>
                  {error}
                </Typography>
              )}

              <Button
                variant="contained"
                color="primary"
                onClick={handleExtract}
                disabled={!file || loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Extraer Datos"}
              </Button>
            </>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "20px",
                  color: "#4caf50",
                }}
              >
                <CheckCircleIcon />
                <Typography variant="body1">Datos extraídos correctamente</Typography>
              </div>

              <Typography variant="subtitle1" gutterBottom>
                Revisa y confirma los datos extraídos:
              </Typography>

              <Grid container spacing={2} style={{ marginTop: "10px" }}>
                {extractedData.fields?.map((field) => (
                  <Grid item xs={12} sm={6} key={field.fieldId}>
                    <TextField
                      label={field.label}
                      value={field.value || ""}
                      onChange={(e) => handleFieldChange(field.fieldId, e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      helperText={
                        field.confidence
                          ? `Confianza: ${Math.round(field.confidence * 100)}%`
                          : undefined
                      }
                    />
                  </Grid>
                ))}
              </Grid>

              {error && (
                <Typography color="error" style={{ marginTop: "20px" }}>
                  {error}
                </Typography>
              )}

              <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setExtractedData(null);
                    setFile(null);
                    setPreview(null);
                  }}
                >
                  Volver a Extraer
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit} style={{ flex: 1 }}>
                  Confirmar y Continuar
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
